import { FractionError } from "@fraction/shared";
import { FractionClientProvider } from "@fraction/shared-frontend";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  type QueryKey,
} from "@tanstack/react-query";
import { clear, createStore, del as idbDel } from "idb-keyval";
import { createContext, type ReactNode } from "react";
import fraction from "src/api/fraction";
import { UI_STATE_KEY } from "src/hooks/useCache";
import { defaultErrorMessageHandler } from "src/lib";
import { queryPersister } from "src/lib/queryPersister";
import { structuralSharingWithDecimals } from "src/lib/structuralSharing";
import { idbDeleteByPrefixSafe } from "src/stores/safeIdb";

// Can be overwritten at a query hook instance level
const MAX_RETRIES = 0;

/** How `experimental_createQueryPersister` keys a row: its prefix, then the query hash verbatim. */
const NATIVE_PDF_PERSISTED_KEY_PREFIX = 'tanstack-query-["native-pdf-';
const NATIVE_PDF_PURGE_FLAG = "native-pdf-persisted-rows-purged";

const getStatusCode = (error: any) => {
  if (error instanceof FractionError) {
    return error.statusCode;
  }
  return error?.response?.statusCode;
};

const errorMessageCache = new Map<QueryKey, number>();

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        console.error(error);
        console.log("Status code:", getStatusCode(error));
        return getStatusCode(error) >= 500 && count < MAX_RETRIES;
      },
      staleTime: 1000 * 20,
      gcTime: 1000 * 60 * 60 * 24,
      structuralSharing: structuralSharingWithDecimals,
      persister: queryPersister.persisterFn,
    },
  },
  queryCache: new QueryCache({
    onError: (err, query) => {
      let timeSet: number;
      if (
        query.meta?.supersedeOtherErrorsInMs &&
        typeof query.meta?.supersedeOtherErrorsInMs === "number" &&
        query.options.queryKey
      ) {
        timeSet = Date.now() + query.meta?.supersedeOtherErrorsInMs;
        errorMessageCache.set(query.options.queryKey, timeSet);
      }

      // wait 25ms before throwing the error in case the cache has come in from elsewhere
      setTimeout(() => {
        const existingCache = query.options.queryKey
          ? errorMessageCache.get(query.options.queryKey)
          : undefined;

        if (!timeSet && existingCache && existingCache > Date.now()) {
          // the error has been superseded, dont run the error handler
          return;
        }

        defaultErrorMessageHandler(err, query.meta?.errorToast as string | undefined);
      }, 25);
    },
  }),
  // Raw @tanstack mutations (e.g. the shared-frontend entity mutations) bypass
  // src/lib's useMutation wrapper and its toast, so they opt in via meta.
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      if (mutation.meta?.errorToast) {
        defaultErrorMessageHandler(err, mutation.meta.errorToast as string);
      }
    },
  }),
});

// persisterFn only persists after queryFn runs — setQueryData writes (optimistic updates) would
// otherwise never reach disk. useCachedState keeps its own copy in localStorage instead. setQueryData dispatches a `success` action
// with `manual: true` (not `setState`), so subscribe to that and persist.
qc.getQueryCache().subscribe((event) => {
  if (
    event?.type === "updated" &&
    event.action.type === "success" &&
    event.action.manual &&
    event.query.queryKey[0] !== UI_STATE_KEY
  ) {
    queryPersister.persistQuery(event.query).catch(() => {});
  }
});

// Backward-compat context — now just provides the same single QueryClient
export const CachedQueryClient = createContext<QueryClient>(qc);

// One-time cleanup of old storage from previous caching implementations
try {
  const oldStore = createStore("fraction-cache", "fraction-cache");
  clear(oldStore).catch(() => {});
  window.caches?.delete("fraction-cache").catch(() => {});
  // Orphaned monolithic blob from the previous PersistQueryClientProvider setup
  idbDel("REACT_QUERY_OFFLINE_CACHE").catch(() => {});
  // Rows the native-PDF queries wrote before they opted out of the persister: base64 page rasters
  // of borrower documents, keyed by a PDFKit handle that dies with the process. Nothing reads those
  // keys any more and the persister only expires an entry it reads, so without this they stay on
  // disk for good. Flagged rather than repeated because it has to enumerate every persisted key.
  if (!localStorage.getItem(NATIVE_PDF_PURGE_FLAG)) {
    idbDeleteByPrefixSafe(NATIVE_PDF_PERSISTED_KEY_PREFIX)
      .then(() => localStorage.setItem(NATIVE_PDF_PURGE_FLAG, "1"))
      .catch(() => {});
  }
} catch {
  // ignore
}

const ReactQueryRoot = ({ children }: { children: ReactNode }) => (
  <QueryClientProvider client={qc}>
    <FractionClientProvider client={fraction}>{children}</FractionClientProvider>
  </QueryClientProvider>
);

export default ReactQueryRoot;
